import { useState, useEffect } from "react";
import { Location } from "history";
import { Link } from "react-router-dom";
import { User } from "firebase/auth";
import { IonText } from "@ionic/react";
import { FormattedMessage } from "react-intl";

import SkeletonText from "../../components/SkeletonText";

import styles from "./styles.module.css";
import "./login.css";

import Auth from "../../api/auth";
import Integrations from "../../api/integrations";

import serialize, { deserialize } from "../../utils/serialize";
import { useAppStore } from "../../store/app";
import GoogleAuthButton from "../../components/common/google-auth";
// import MicrosoftAuthButton from "../../components/common/microsoft-auth";
import Divider from "./Divider";
import PasswordAuth from "../../components/common/password-auth";
import { useAuth } from "../../providers/auth";

interface Properties {
  name: string;
}

interface Company {
  code: string;
  locations: Properties[];
}

interface Error {
  message: string;
}

const Register: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const [logInError, setLogInError] = useState(null);
  const [CBError, setCBError] = useState("");
  const [CBSuccess, setCBSuccess] = useState<Company>({
    code: "",
    locations: [],
  });
  const [loading, setLoading] = useState(false);
  const [CBLoading, setCBLoading] = useState(false);
  const [registerLink, setRegisterLInk] = useState("");
  const { refreshUser } = useAuth();
  const language = useAppStore((state) => state.user.language);

  useEffect(() => {
    const { search } = props.location;
    const query = deserialize(search);

    if (query.code) {
      requestCBInfo(query.code);
    } else {
      setCBError("Please try to connect again from cloudbeds dashboard");
    }
    const queryString = serialize(query);
    setRegisterLInk(`/auth/login?pathname=/cloudbeds&${queryString}`);
  }, [props.location]);

  const requestCBInfo = async (code: string) => {
    setCBLoading(true);
    const { data, err } = await Integrations.cloudbedsPreviewCode(code);
    if (err) {
      setCBError(err.message || "Something went wrong");
    }
    if (data) {
      setCBSuccess({
        code: data.company.code,
        locations: data.company.locations,
      });
    }
    setCBLoading(false);
  };

  const registerApi = async (
    auth: User,
    type?: "password" | "google" | "microsoft",
  ) => {
    setLoading(() => true);
    const { data, err } = await Auth.register({
      email: auth.email,
      language,
      companyCode: CBSuccess.code,
      auth: {
        provider: type || "password",
        uid: auth.uid,
      },
    });

    if (data) {
      refreshUser(); // refetch user after registration in DB succeded
    }
    if (err) {
      let message = err.message;
      if (err.details && err.details.body && err.details.body.length) {
        err.details.body.forEach((e: Error) => {
          message = message + " " + e.message;
        });
      }
      // Delete the user from firebase if somehting went wrong
      await auth?.delete();
      setLogInError(() => message);
    }
    setLoading(() => false);
  };

  const form = (
    <>
      <div className="visito-card visito-block mb-4 mt-4">
        <div className="visito-card-body bg-suttle">
          <div className="color-step-700">
            {CBSuccess.locations.map((p, k) => (
              <div key={k}>{p.name}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-column gap-3">
        <GoogleAuthButton type="sign-up" onSuccess={registerApi} />
        {/*
        <MicrosoftAuthButton
          type="sign-up"
          onSuccess={registerApi}
        />
        */}
      </div>

      <Divider />

      <PasswordAuth
        type="sign-up"
        loading={loading}
        registerLink={registerLink}
        error={logInError}
        onSuccess={registerApi}
      />
    </>
  );

  const error = (
    <>
      <IonText className="mb-1" color="danger">
        {CBError}
      </IonText>
      <hr />
      <div className="color-step-500 text-center">
        <Link to="/auth/login">
          <FormattedMessage id="auth.register.login" />
        </Link>{" "}
        <FormattedMessage id="common.or" />{" "}
        <Link to="/auth/register">
          <FormattedMessage id="auth.login.register" />
        </Link>
      </div>
    </>
  );

  return (
    <div className={styles.authHolder}>
      <div className={styles.authPanel}>
        <h1>
          {" "}
          <FormattedMessage id="auth.register.titleCloudbeds" />
        </h1>
        {CBError ? error : null}
        {CBLoading && <SkeletonText rows={6} />}
        {CBSuccess.code ? form : null}
      </div>
    </div>
  );
};

interface ILayoutProps {
  location: Location;
}

export default Register;
